import { frames } from "./cyberboardR6CdcProtocol";
import { getR6SwitchLightPageIndices } from "../r6Profile";

const ZERO_KEY = "#00000000";

function list(value) {
  return Array.isArray(value) ? value : [];
}

function enabled(value) {
  return value === true || value === 1 || value === "1";
}

function validFlag(value) {
  if (value === 2 || value === "2") return 2;
  return enabled(value) ? 1 : 0;
}

function uint16BE(value) {
  const number = Number(value) || 0;
  return [(number >> 8) & 0xff, number & 0xff];
}

function uint16LE(value) {
  const number = Number(value) || 0;
  return [number & 0xff, (number >> 8) & 0xff];
}

function byteList(value, digits) {
  if (typeof value !== "string") {
    throw new TypeError("R6 JSON hex value must be a string");
  }
  const hex = value.replace(/^#|^0x/i, "");
  if (!new RegExp(`^[0-9a-f]{${digits}}$`, "i").test(hex)) {
    throw new TypeError(`invalid R6 JSON hex value: ${value}`);
  }
  const result = [];
  for (let index = 0; index < hex.length; index += 2) {
    result.push(parseInt(hex.slice(index, index + 2), 16));
  }
  return result;
}

function bytesForColor(value) {
  return byteList(value || "#000000", 6);
}

function bytesForKey(value) {
  return byteList(value || ZERO_KEY, 8);
}

function chunks(items, size) {
  const result = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
}

function hasFeature(features, feature) {
  return !features || features.includes(feature);
}

function validateFrameCollection(value, name, rgbLength, exactLength = true) {
  if (!value || validFlag(value.valid) !== 1) return;
  const frameCount = Number(value.frame_num);
  const frameData = list(value.frame_data);
  if (
    !Number.isInteger(frameCount) ||
    frameCount < 0 ||
    frameCount !== frameData.length
  ) {
    throw new TypeError(
      `R6 JSON ${name}.frame_num must match frame_data length`
    );
  }
  frameData.forEach((item, index) => {
    if (!item || Number(item.frame_index) !== index) {
      throw new TypeError(`R6 JSON ${name}.frame_index must be sequential`);
    }
    if (
      !Array.isArray(item.frame_RGB) ||
      (exactLength
        ? item.frame_RGB.length !== rgbLength
        : item.frame_RGB.length > rgbLength)
    ) {
      throw new TypeError(
        exactLength
          ? `R6 JSON ${name}.frame_RGB must contain ${rgbLength} colors`
          : `R6 JSON ${name}.frame_RGB must contain no more than ${rgbLength} colors`
      );
    }
    item.frame_RGB.forEach((color) => bytesForColor(color));
  });
}

function validateFullProfile(data) {
  const pageCount = Number(data.page_num);
  const pages = list(data.page_data);
  if (
    !Number.isInteger(pageCount) ||
    pageCount < 0 ||
    pages.length !== pageCount
  ) {
    throw new TypeError("R6 JSON full profile page_data must match page_num");
  }
  const pageIndexes = new Set();
  pages.forEach((page, index) => {
    const pageIndex = Number(page?.page_index);
    if (!Number.isInteger(pageIndex) || pageIndex !== index) {
      throw new TypeError("R6 JSON full profile page_index must be sequential");
    }
    pageIndexes.add(pageIndex);
    const wordPage = page.word_page || {};
    if (validFlag(wordPage.valid) === 1) {
      const wordLength = Number(wordPage.word_len);
      const unicode = list(wordPage.unicode);
      if (
        !Number.isInteger(wordLength) ||
        wordLength < 0 ||
        unicode.length < wordLength
      ) {
        throw new TypeError("R6 JSON word_page is incomplete");
      }
      unicode.slice(0, wordLength).forEach((value) => byteList(value, 4));
    }
    validateFrameCollection(page.frames, `page_data[${index}].frames`, 200);
    validateFrameCollection(
      page.keyframes,
      `page_data[${index}].keyframes`,
      90
    );
    validateFrameCollection(
      page.spotlight_frames,
      `page_data[${index}].spotlight_frames`,
      112,
      false
    );
  });
  if (pageIndexes.size !== pageCount) {
    throw new TypeError("R6 JSON full profile contains duplicate page_index");
  }

  const keyLayer = data.key_layer || {};
  if (enabled(keyLayer.valid)) {
    const layers = list(keyLayer.layer_data);
    if (Number(keyLayer.layer_num) !== layers.length) {
      throw new TypeError("R6 JSON key_layer.layer_num must match layer_data");
    }
    layers.forEach((layer, index) => {
      if (!Array.isArray(layer?.layer) || !layer.layer.length) {
        throw new TypeError(`R6 JSON key_layer.layer_data[${index}] is empty`);
      }
      layer.layer.forEach((key) => bytesForKey(key));
    });
  }
}

function completeDownloadPages(data) {
  // Selection exports are sparse; keep wire slots aligned to page_index.
  if (data.page_data !== undefined && !Array.isArray(data.page_data)) {
    throw new TypeError("R6 JSON page_data must be an array");
  }
  const source = list(data.page_data);
  const declaredCount = Number(data.page_num);
  if (
    data.page_num !== undefined &&
    data.page_num !== null &&
    data.page_num !== "" &&
    (!Number.isInteger(declaredCount) ||
      declaredCount < 0 ||
      declaredCount > 0xff)
  ) {
    throw new TypeError("R6 JSON page_num must be an unsigned byte");
  }
  const indexes = source.map((page, index) => {
    if (!page || typeof page !== "object" || Array.isArray(page)) {
      throw new TypeError("R6 JSON page_data entries must be objects");
    }
    const pageIndex = Number(page?.page_index ?? index);
    if (!Number.isInteger(pageIndex) || pageIndex < 0) {
      throw new TypeError("R6 JSON page_index must be a non-negative integer");
    }
    return pageIndex;
  });
  const pageCount =
    Number.isInteger(declaredCount) && declaredCount > 0
      ? declaredCount
      : indexes.length
      ? indexes.reduce((max, index) => Math.max(max, index), -1) + 1
      : 0;
  if (pageCount > 0xff) {
    throw new RangeError("R6 JSON page count exceeds the protocol limit");
  }
  if (indexes.some((index) => index >= pageCount)) {
    throw new RangeError("R6 JSON page_index exceeds page_num");
  }

  const pagesByIndex = new Map();
  source.forEach((page, index) => {
    const pageIndex = indexes[index];
    if (pagesByIndex.has(pageIndex)) {
      throw new TypeError(`R6 JSON duplicate page_index: ${pageIndex}`);
    }
    pagesByIndex.set(pageIndex, page);
  });

  const pageData = Array.from({ length: pageCount }, (_, pageIndex) => {
    const emptyPage = {
      valid: 2,
      page_index: pageIndex,
      lightness: 0,
      speed_ms: 0,
      color: { default: false, back_rgb: "#000000", rgb: "#000000" },
      word_page: { valid: 2, word_len: 0, unicode: [] },
      frames: { valid: 2, frame_num: 0, frame_data: [] },
      keyframes: { valid: 2, frame_num: 0, frame_data: [] },
    };
    const page = pagesByIndex.get(pageIndex);
    if (!page) return emptyPage;
    return {
      ...emptyPage,
      ...page,
      page_index: pageIndex,
      color: { ...emptyPage.color, ...page.color },
      word_page: { ...emptyPage.word_page, ...page.word_page },
      frames: { ...emptyPage.frames, ...page.frames },
      keyframes: { ...emptyPage.keyframes, ...page.keyframes },
    };
  });

  return { ...data, page_num: pageCount, page_data: pageData };
}

function validOrIgnore(value) {
  return value === undefined || value === null ? 2 : validFlag(value);
}

function validBits(data, features) {
  const result = [];
  list(data.page_data).forEach((page) => {
    // Partial transactions mark every unrelated page section as 0x02 (ignore).
    result.push(
      hasFeature(features, "pageControl") ? validOrIgnore(page.valid) : 2,
      hasFeature(features, "pageText")
        ? validOrIgnore(page.word_page?.valid)
        : 2,
      hasFeature(features, "pageRgb") ? validOrIgnore(page.frames?.valid) : 2,
      hasFeature(features, "axisRgb") ? validOrIgnore(page.keyframes?.valid) : 2
    );
  });
  result.push(
    hasFeature(features, "keyLayer") ? validOrIgnore(data.key_layer?.valid) : 2
  );
  return result;
}

function buildUncertainty(data, result, features) {
  const payload = [];
  list(data.page_data).forEach((page) => {
    const frameCount =
      validFlag(page.frames?.valid) === 1 ? page.frames?.frame_num || 0 : 0;
    const keyFrameCount =
      validFlag(page.keyframes?.valid) === 1
        ? page.keyframes?.frame_num || 0
        : 0;
    const wordLength =
      hasFeature(features, "pageText") &&
      validFlag(page.word_page?.valid) === 1
        ? page.word_page?.word_len || 0
        : 0;
    payload.push(
      page.page_index || 0,
      wordLength,
      ...uint16LE(frameCount),
      ...uint16LE(keyFrameCount)
    );
  });
  result.push(frames.uncertaintyInfo(data.page_num || 0, payload));
}

function buildPageControls(data, result) {
  const pages = list(data.page_data);
  const frameCount = Math.ceil((Number(data.page_num) || pages.length) / 4);
  for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
    const group = pages.slice(frameIndex * 4, frameIndex * 4 + 4);
    const payload = [];
    group.forEach((page) => {
      const control = page.page_control || page;
      const color = control.color || {};
      const speed = control.speed_ms || 0;
      // 02 05 carries the ignore flag; 02 02 keeps each page slot.
      payload.push(
        validFlag(control.valid) === 2 ? 0 : validFlag(control.valid),
        control.page_index ?? page.page_index ?? 0,
        control.lightness || 0,
        ...uint16LE(speed),
        validFlag(color.default),
        ...bytesForColor(color.back_rgb),
        ...bytesForColor(color.rgb)
      );
    });
    result.push(
      frames.pageControl(frameCount, frameIndex, group.length, payload)
    );
  }
}

function buildWordPages(data, result) {
  list(data.page_data).forEach((page) => {
    const wordPage = page.word_page || {};
    const unicode = list(wordPage.unicode);
    const wordLength = wordPage.word_len || 0;
    if (!wordLength || validFlag(wordPage.valid) !== 1) return;
    chunks(unicode.slice(0, wordLength), 28).forEach((group, frameIndex) => {
      const payload = [];
      group.forEach((item) => payload.push(...byteList(item, 4)));
      result.push(
        frames.wordPage(
          frameIndex,
          page.page_index || 0,
          validFlag(wordPage.valid),
          group.length,
          payload
        )
      );
    });
  });
}

function rgbBytes(items) {
  const result = [];
  list(items).forEach((item) => result.push(...bytesForColor(item)));
  return result;
}

function buildRgbFrames(data, result) {
  list(data.page_data).forEach((page) => {
    const frameData = page.frames || {};
    if (!frameData.frame_num || validFlag(frameData.valid) !== 1) return;
    list(frameData.frame_data).forEach((item) => {
      const bytes = rgbBytes(item.frame_RGB);
      for (let index = 0; index < 11; index += 1) {
        result.push(
          frames.rgbFrame(
            page.page_index || 0,
            item.frame_index || 0,
            index,
            bytes.slice(index * 56, index === 10 ? 600 : (index + 1) * 56)
          )
        );
      }
    });
  });
}

function buildKeyFrames(data, result) {
  list(data.page_data).forEach((page) => {
    const frameData = page.keyframes;
    if (!frameData || !frameData.frame_num || validFlag(frameData.valid) !== 1)
      return;
    list(frameData.frame_data).forEach((item) => {
      const bytes = rgbBytes(item.frame_RGB);
      for (let index = 0; index < 5; index += 1) {
        result.push(
          frames.keyFrame(
            page.page_index || 0,
            item.frame_index || 0,
            index,
            bytes.slice(index * 56, index === 4 ? 270 : (index + 1) * 56)
          )
        );
      }
    });
  });
}

function buildKeyFunctions(data, result) {
  const exchange = list(data.exchange_key);
  exchange.forEach((item) => {
    const inputKeys = list(item.input_key)
      .slice(0, 5)
      .map((key) => {
        bytesForKey(key);
        return key;
      });
    const outputKeys = list(item.out_key)
      .slice(0, 5)
      .map((key) => {
        bytesForKey(key);
        return key;
      });
    result.push(
      frames.exchangeKey(data.exchange_num || 0, {
        exchange_index: item.exchange_index || 0,
        input_key: inputKeys,
        out_key: outputKeys,
      })
    );
  });

  const fnKeys = list(data.Fn_key);
  chunks(fnKeys, 6).forEach((group) => {
    result.push(frames.fnKey(data.Fn_key_num || fnKeys.length, group));
  });

  const swapKeys = list(data.swap_key);
  chunks(swapKeys, 6).forEach((group) => {
    result.push(frames.swapKey(data.swap_key_num || swapKeys.length, group));
  });
}

function macroEntries(data) {
  const source = list(data.macro_key).length
    ? data.macro_key
    : list(data.MACRO_key);
  return source.map((item, index) => ({
    // R6 的宏槽位由触发键 #009515XX 决定，避免新增宏追加到数组末尾后复用旧索引。
    MACRO_key_index: macroIndex(item, index),
    input_key:
      item.input_key ||
      item.original_key ||
      `#009515${index.toString(16).toUpperCase().padStart(2, "0")}`,
    out_key: list(item.out_key || item.layer_key),
    intvel_ms: list(item.intvel_ms),
  }));
}

function macroIndex(item, fallback) {
  const inputKey = item.input_key || item.original_key;
  const match =
    typeof inputKey === "string" && inputKey.match(/^#009515([0-9a-f]{2})$/i);
  return match ? parseInt(match[1], 16) : item.MACRO_key_index ?? fallback;
}

function buildMacros(data, result) {
  const macros = macroEntries(data);
  const packets = [];
  macros.forEach((item) => {
    // 空宏也要保留一个占位分包，让设备同步到该宏槽位的清空状态。
    const groups = item.out_key.length ? chunks(item.out_key, 8) : [[]];
    groups.forEach((group, packetIndex) => {
      packets.push({
        item,
        packetIndex,
        out_key: group,
        intvel_ms: item.intvel_ms.slice(packetIndex * 8, packetIndex * 8 + 8),
      });
    });
  });
  packets.forEach((packet, index) => {
    result.push(
      frames.macroKeyDownload(
        packets.length,
        index,
        {
          ...packet.item,
          out_key: packet.out_key,
          intvel_ms: packet.intvel_ms,
        },
        packet.packetIndex
      )
    );
  });
}

function buildKeyLayer(data, result) {
  const keyLayer = data.key_layer;
  if (!keyLayer || !enabled(keyLayer.valid)) return;
  result.push(frames.keyLayerControl(keyLayer.layer_num || 0));
  const bytes = [];
  list(keyLayer.layer_data).forEach((item) => {
    list(item.layer).forEach((key) => bytes.push(...byteList(key, 8)));
  });
  chunks(bytes, 60).forEach((group, index) => {
    result.push(frames.keyLayerData(index, group));
  });
}

function buildPressHold(data, result) {
  const items = list(data.tab_key);
  items.forEach((item, index) => {
    if (!item.first_key) return;
    result.push(
      frames.pressHold({
        ...item,
        pack_total_num: items.length,
        pack_num: index,
        ph_key_data: item.ph_key_data || ZERO_KEY,
        first_key: item.first_key,
        second_key: item.second_key || ZERO_KEY,
        interval_time: item.interval_time || 0,
      })
    );
    if (item.old_key && item.new_key) {
      result.push(
        frames.changeKey({
          layer_num: item.layer_num || 0,
          row_num: item.row_num || 0,
          index: item.index || 0,
          old_key: item.old_key,
          new_key: item.new_key,
        })
      );
    }
  });
}

function buildCarLights(data, result, features, fullTransaction = false) {
  const sourcePages = list(data.page_data);
  const pages = getR6SwitchLightPageIndices().map((pageIndex) =>
    sourcePages.find(
      (page, index) => Number(page.page_index ?? index) === pageIndex
    )
  );
  if (!pages.some((page) => page?.spotlight_frames)) return;

  const info = [];
  pages.forEach((page) => {
    const spotlight = page?.spotlight_frames;
    const valid = spotlight ? validFlag(spotlight.valid) : features ? 2 : 0;
    const frameCount =
      fullTransaction || valid === 1 ? spotlight?.frame_num || 0 : 0;
    info.push(valid, ...uint16BE(frameCount));
  });
  result.push(frames.carLightInfo(info));

  pages.forEach((page) => {
    const spotlight = page?.spotlight_frames;
    if (!spotlight) return;
    if (
      !spotlight.frame_num ||
      (!fullTransaction && validFlag(spotlight.valid) !== 1)
    )
      return;
    list(spotlight.frame_data).forEach((item) => {
      const bytes = rgbBytes(item.frame_RGB);
      for (let index = 0; index < 2; index += 1) {
        result.push(
          frames.carLightData(
            page.page_index || 0,
            item.frame_index || 0,
            index,
            bytes.slice(index * 56, (index + 1) * 56)
          )
        );
      }
    });
  });
}

export function buildR6Download(data, features) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new TypeError("R6 JSON configuration must be an object");
  }
  const fullTransaction =
    !features ||
    (features.includes("keyFunctions") && features.includes("keyLayer"));
  const downloadData = completeDownloadPages(data);
  const partialTransaction = Boolean(data.__r6PartialImport);
  const transportData =
    fullTransaction || partialTransaction
      ? JSON.parse(JSON.stringify(downloadData))
      : downloadData;
  if (fullTransaction) {
    // AM Master transmits retained payloads too; 02 05 alone controls applying them.
    transportData.page_data.forEach((page) => {
      if (validFlag(page.valid) === 2) page.valid = 1;
      if (page.page_control && validFlag(page.page_control.valid) === 2) {
        page.page_control.valid = 1;
      }
      ["frames", "keyframes", "spotlight_frames"].forEach((section) => {
        if (page[section]?.frame_num) page[section].valid = 1;
      });
      if (page.word_page?.word_len) page.word_page.valid = 1;
    });
    if (validFlag(transportData.key_layer?.valid) === 2) {
      transportData.key_layer.valid =
        transportData.key_layer.layer_num > 0 ? 1 : 0;
    }
    validateFullProfile(transportData);
  }
  if (partialTransaction && !fullTransaction) {
    // AM Master sends retained RGB payloads; 02 05 still decides whether
    // ignored sections are applied.
    transportData.page_data.forEach((page) => {
      ["frames", "keyframes", "spotlight_frames"].forEach((section) => {
        if (page[section]?.frame_num) page[section].valid = 1;
      });
    });
  }
  const result = [];
  const hasPageData = [
    "pageControl",
    "pageText",
    "pageRgb",
    "axisRgb",
  ].some((feature) => hasFeature(features, feature));
  if (!features || hasPageData)
    buildUncertainty(transportData, result, features);
  if (hasFeature(features, "pageControl"))
    buildPageControls(transportData, result);
  if (hasFeature(features, "pageText")) buildWordPages(transportData, result);
  if (hasFeature(features, "pageRgb")) buildRgbFrames(transportData, result);
  if (hasFeature(features, "axisRgb")) buildKeyFrames(transportData, result);
  if (hasFeature(features, "keyFunctions"))
    buildKeyFunctions(transportData, result);
  if (hasFeature(features, "macro")) buildMacros(transportData, result);
  if (hasFeature(features, "keyLayer")) {
    if (fullTransaction && !enabled(transportData.key_layer?.valid)) {
      result.push(frames.keyLayerControl(0));
    } else {
      buildKeyLayer(transportData, result);
    }
  }
  if (hasFeature(features, "pressHold")) buildPressHold(transportData, result);
  if (hasFeature(features, "carLight"))
    buildCarLights(
      downloadData,
      result,
      features,
      fullTransaction || partialTransaction
    );
  return {
    frames: result,
    pageCount: downloadData.page_num || 0,
    validBits: validBits(downloadData, features),
  };
}
